import { Controller } from "@hotwired/stimulus"

// Minimal Stimulus controller for Salesforce Environment form
// Following Rails 8 + Hotwire best practices: server-side validation, minimal JavaScript
export default class extends Controller {
  static targets = ["submitButton"]

  // Real-time field validation via server-side endpoint
  async validateField(event) {
    const fieldName = event.target.dataset.fieldName
    if (!fieldName) return

    const formData = new FormData(this.element)
    formData.append("field_name", fieldName)
    
    try {
      const response = await fetch(`${this.element.action}/validate_field`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: formData
      })
      
      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      }
    } catch (error) {
      console.error("Validation error:", error)
    }
  }

  // Test connection with loading state
  async validateForm(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const originalText = button.innerHTML
    
    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Testing Connection...
    `

    try {
      const formData = new FormData(this.element)
      const testUrl = `${this.element.action.replace('/edit', '')}/test_connection`
      console.log('Testing connection at URL:', testUrl)
      
      const response = await fetch(testUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          "Accept": "application/json"
        },
        body: formData
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.showNotification("Connection test successful!", "success")
      } else {
        this.showNotification(`Connection failed: ${result.error || result.message}`, "error")
      }
    } catch (error) {
      this.showNotification("Connection test failed. Please check your settings.", "error")
      console.error("Connection test error:", error)
    } finally {
      // Restore button state
      button.disabled = false
      button.innerHTML = originalText
    }
  }

  // Simple notification system
  showNotification(message, type = "info") {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 max-w-sm rounded-lg p-4 shadow-lg transition-all duration-300 opacity-0 ${
      type === 'success' ? 'bg-green-100 border border-green-200 text-green-800' :
      type === 'error' ? 'bg-red-100 border border-red-200 text-red-800' :
      'bg-blue-100 border border-blue-200 text-blue-800'
    }`
    
    notification.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          ${type === 'success' ? 
            '<svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' :
            type === 'error' ?
            '<svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>' :
            '<svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg>'
          }
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium">${message}</p>
        </div>
      </div>
    `

    document.body.appendChild(notification)

    // Fade in
    setTimeout(() => notification.classList.remove('opacity-0'), 100)
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}